import React, { useEffect, useState, useCallback } from 'react';
import useCss from '../../../../Hooks/useCss';
import { useSelector, shallowEqual } from 'react-redux';
import { ReactSVG } from 'react-svg';
import Ratio from 'react-ratio';
import Asset, { useAsset } from '../../Asset/Asset';
import Image from '../../Asset/Image/Image';
import { pseudolocalize } from '../../common/pseudolocalize/pseudolocalize';
import ExpandIcon from '../ExpandIcon/ExpandIcon';
import Loader from '../../common/Loading/Loading';

import './Svg.scss';

const SvgDynamicImageItem = ({ displayid, id }) => {
  const asset = useAsset(id);

  return (
    <Image
      id={displayid}
      src={`${asset.file}`}
      alt={asset.description}
      zoom={false}
      className="dynamic-image-item"
      assetId={id}
    />
  );
};
const SvgDynamicImages = ({ dimensions, images, stack }) => {
  return (
    <div
      className="dynamic-images"
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        zIndex: `${stack && stack.toLowerCase() === 'beforeend' ? '3' : '1'}`
      }}
    >
      {images.map((image, imageIndex) => {
        return (
          <SvgDynamicImageItem
            key={imageIndex}
            displayid={image.importId ? image.importId : image.id}
            id={image.id ? image.id : image}
          />
        );
      })}
    </div>
  );
};

const SvgDynamicText = ({ dimensions, svg }) => {
  const { isPseudo, assetManifest } = useSelector(
    state => ({
      isPseudo: state.LanguageState.isPseudo,
      assetManifest: state.BlockState.assetManifest
    }),
    shallowEqual
  );

  return (
    <div
      id="dynamic-text"
      className="dynamic-text"
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`
      }}
    >
      {Object.keys(svg).map(key => {
        if (key !== 'file' && key !== 'caption') {
          let keyAsset = assetManifest.assets.find(a => a.id === svg[key]);
          // if the asset is missing, the page tanks
          // This helps find and fix them
          if (!keyAsset) {
            // eslint-disable-next-line no-console
            console.log('MISSING ASSET :', key);
            return (
              <span className="label label--danger label--large ">{`MISSING ASSET: ${key}`}</span>
            );
          }
          let content = keyAsset.content.replace(/className/g, 'class');
          // assuming we have an asset, display it
          return (
            <span
              key={key}
              id={key}
              className="dynamic-text-item"
              dangerouslySetInnerHTML={{
                __html: isPseudo ? pseudolocalize(content) : content
              }}
            />
          );
        } else {
          return false;
        }
      })}
    </div>
  );
};

// Handles loading the SVG
const SvgLoader = React.memo(({ svg, handleResize, useImg }) => {
  // let svgAsset = assetManifest.assets.find(a => a.id === svgs[0].file);
  const svgAsset = useAsset(svg.file);

  // calls and listens for resize
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return useImg ? (
    <img src={svgAsset.file} alt="" />
  ) : (
    <ReactSVG
      src={svgAsset.file}
      beforeInjection={svg => {
        handleResize();
      }}
    />
  );
});
// primary svg container
const Svg = ({
  svgs,
  ratio,
  importId,
  stylesheet,
  uid,
  images,
  useImg,
  imageStack
}) => {
  // originally thought we'd support multiple SVGs in one media.yml. Nope.
  const svg = svgs[0];
  const id = importId ? importId : uid;
  // const cssloaded = false;
  // load stylesheet
  const cssloaded = useCss({
    id: id,
    type: 'svg',
    stylesheet: stylesheet
  });

  // state flag for better loading
  const [loading, setLoading] = useState(true);

  // initial dimensions from ratio
  const [dimensions] = useState({
    width: ratio.split('/')[0],
    height: ratio.split('/')[1]
  });

  // handle the resize and scaling
  const handleResize = useCallback(() => {
    let parent = document.getElementById(`${id}`);
    // check for a parent. Sometimes React can't find it (might be an issue with svg-react).
    // Switching languages tends to miss the parent, but if it can't find it, skipping the resize
    // doesn't seem to have any side effects.
    if (parent) {
      let parentDimensions = parent.getBoundingClientRect();
      let parentWidth = parentDimensions.width;
      let parentHeight = parentDimensions.height;
      parent.querySelector('.dynamic-text').style.transform = `scale(${
        parentWidth / dimensions.width
      }, ${parentHeight / dimensions.height})`;
      setLoading(false);

      if (images && images.length) {
        parent.querySelector('.dynamic-images').style.transform = `scale(${
          parentWidth / dimensions.width
        }, ${parentHeight / dimensions.height})`;
        setLoading(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={'svg-container card'} tabIndex="-1" aria-hidden="true">
      <ExpandIcon id={`expandIco-${id}`} isSvg={true} />
      <Ratio ratio={ratio ? ratio.split('/')[0] / ratio.split('/')[1] : 1}>
        <div id={id} className="svg-wrapper">
          <SvgLoader
            svg={svg}
            useImg={useImg}
            dimensions={dimensions}
            setLoading={setLoading}
            handleResize={handleResize}
          />
          {images && images.length > 0 && (
            <SvgDynamicImages
              dimensions={dimensions}
              images={images}
              stack={imageStack}
            />
          )}
          <SvgDynamicText dimensions={dimensions} svg={svgs[0]} />
        </div>
        {loading && !cssloaded && <Loader context="SVG" />}
      </Ratio>

      {svg.caption && (
        <div className="card__body" key={svg.caption}>
          <Asset id={svg.caption} />
        </div>
      )}
    </div>
  );
};

export default React.memo(Svg);
